/**
 * ewk — EWKFondsG-Abgabe (Verpackungsabgabe) je Kategorie und Variante.
 *
 * Konservative Berechnung: die volle Masse einer Variante wird je
 * ausgewählter Kategorie mit deren Abgabesatz multipliziert (kein
 * Aufteilen der Masse auf mehrere Materialien). Reine Funktionen,
 * kein React, kein I/O.
 */

const MAX_KURZ_LAENGE = 18;

function findKategorie(schluessel, kategorien = []) {
  return kategorien.find((k) => k.schluessel === schluessel) || null;
}

/** Abgabesatz (€/kg) für einen Schlüssel, null wenn unbekannt oder ohne Satz */
export function rateFor(schluessel, kategorien = []) {
  const kat = findKategorie(schluessel, kategorien);
  if (!kat) return null;
  const satz = kat.abgabesatz_eur_kg;
  if (satz == null) return null;
  const n = Number(satz);
  return Number.isFinite(n) ? n : null;
}

function isMissingGewicht(gewichtKg) {
  if (gewichtKg == null || gewichtKg === '') return true;
  const n = Number(gewichtKg);
  return !Number.isFinite(n) || n <= 0;
}

/**
 * Abgabe je Stück berechnen.
 * @returns {{ gesamt: number, positionen: Array, unvollstaendig: boolean, fehltGewicht: boolean }}
 */
export function abgabeProStueck({ gewichtKg, anteilProzent = 100, schluessel = [], kategorien = [] } = {}) {
  const fehltGewicht = isMissingGewicht(gewichtKg);
  const anteil = anteilProzent == null || anteilProzent === '' ? 100 : Number(anteilProzent);
  const anteilSicher = Number.isFinite(anteil) ? anteil : 100;
  const masse = fehltGewicht ? 0 : Number(gewichtKg) * (anteilSicher / 100);

  let unvollstaendig = false;
  const positionen = (schluessel || []).map((s) => {
    const kat = findKategorie(s, kategorien);
    const satz = rateFor(s, kategorien);
    const name = kat ? kat.name : s;
    if (satz == null) unvollstaendig = true;
    const betrag = fehltGewicht || satz == null ? 0 : masse * satz;
    return { schluessel: s, name, satz, betrag };
  });

  const gesamt = positionen.reduce((sum, p) => sum + p.betrag, 0);

  return { gesamt, positionen, unvollstaendig, fehltGewicht };
}

/**
 * Abgabe je Variante eines Produkts.
 * @returns {Map<string, ReturnType<typeof abgabeProStueck>>}
 */
export function abgabeProProdukt(product, variants = [], kategorien = []) {
  const schluesselListe = Array.isArray(product?.ewk_kategorien) ? product.ewk_kategorien : [];
  const anteilProzent = product?.ewk_kunststoffanteil_prozent;
  const map = new Map();
  for (const variant of variants || []) {
    if (variant.aktiv === false) continue;
    const result = abgabeProStueck({
      gewichtKg: variant.gewicht_kg,
      anteilProzent,
      schluessel: schluesselListe,
      kategorien,
    });
    map.set(variant.id, result);
  }
  return map;
}

/** Formatiert einen Abgabebetrag mit 4 Nachkommastellen, z. B. '0,0885 €' */
export function formatAbgabe(betrag) {
  const n = Number(betrag);
  if (betrag == null || !Number.isFinite(n)) return '—';
  return `${n.toLocaleString('de-DE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} €`;
}

function kuerzeName(name) {
  const ohneKlammer = name.replace(/\s*\([^)]*\)\s*$/, '');
  if (ohneKlammer.length > MAX_KURZ_LAENGE) {
    return `${ohneKlammer.slice(0, MAX_KURZ_LAENGE)}…`;
  }
  return ohneKlammer;
}

/** Kurz-Badges für eine Liste von Kategorie-Schlüsseln */
export function ewkBadgeLabels(schluesselList, kategorien = []) {
  if (!schluesselList || schluesselList.length === 0) return [];
  return schluesselList.map((s) => {
    const kat = findKategorie(s, kategorien);
    const name = kat ? kat.name : s;
    return { schluessel: s, name, kurz: kuerzeName(name) };
  });
}

/** Suchtext (kleingeschrieben) aus den Kategorienamen für die Listensuche */
export function ewkSuchtext(schluesselList, kategorien = []) {
  if (!schluesselList || schluesselList.length === 0) return '';
  return schluesselList
    .map((s) => {
      const kat = findKategorie(s, kategorien);
      return kat ? kat.name : s;
    })
    .join(' ')
    .toLowerCase();
}
